import React, { useMemo, useCallback, useState, useContext, useEffect } from 'react';
import { Rate } from 'antd';
import { useCourseDetailsQuery } from '../../../gql/hooks';
import { Translate } from 'app/services';
import { Button, Row, Col, Form } from 'antd';
import { LearningObjectItem } from './components';
import { PageContainer, PageContent } from 'app/components/common';
import { ActionButton, Link, SimpleCollapse, TextArea } from '../../common';
import { getDefaultCourseBanner } from '../../../const';
import { submitCourseRating } from '../../../gql';
import { LoadingPopup } from '../../common/learningListController/loadingPopup/LoadingPopup';
import { ACTIVE_THEME_NAME } from '../../../branding';
import Modal from 'antd/lib/modal/Modal';
import './styles.less';
import Certificate from '../../common/certificate';
import { InfoBanner } from '../../common/InfoBanner';
import ResourceList from '../../common/resourceList';
import { BreadcrumbContext, useBreadcrumbs } from '../../../breadcrumbs/BreadcrumbContext';
import { PaperIcon } from '@nackle/paper';
import { getString } from '@nackle/intl-tools';
import { Navigation, Routes } from '../../../navigation';
import { CourseUtils } from '../../../utils';


export const CourseDetailsPage = ( props ) => {
    const courseId = useMemo( () => props?.route?.params?.courseId, [ props ] );
    
    const { data, loading, refetch, error } = useCourseDetailsQuery( courseId );

    const { leaveCrumb } = useContext( BreadcrumbContext );
    const breadcrumbs = useBreadcrumbs();

    useEffect( () => {
        if ( data?.name ) {
            leaveCrumb( props.pageKey, data.name );
        }
    }, [ data?.name ] );

    useEffect( () => {
        if ( data?.review?.rating || data?.review?.review ) {

            ratingFormController.setFieldsValue( {
                rating: data?.review?.rating, 
                review: data?.review?.review
            } );
            setDisableForm( true );
        }
    }, [ data?.review ] );

    useEffect( () => {
        if ( data?.ratingAverage > 0 ) {
            setAverageRating( data.ratingAverage );
        }
    }, [ data?.ratingAverage ] );

    const [ averageRating, setAverageRating ] = useState( 0 );

    const [ isShowingRatingForm, setShowRatingForm ] = useState( false );
    const [ ratingFormData, setRatingFormData ] = useState( { rating: undefined, review: undefined } );
    const [ ratingFormLoading, setRatingFormLoading ] = useState( false );
    const [ disableForm, setDisableForm ] = useState( false );

    const [ ratingFormController ] = Form.useForm();

    const updateFormValues = useCallback( ( values ) => {
        setRatingFormData( { ...ratingFormData, ...values } );
    }, [ ratingFormData, setRatingFormData ] );

    /**
     * Closes the rating form on the page and clears the cached data.
     */
    const closeRatingForm = useCallback( () => {
        setShowRatingForm( false );
        setDisableForm( true );
    }, [ setShowRatingForm, ratingFormController ] );

    /**
     * Attempts to submit the user's rating for the course.
     */
    const handleRatingSubmission = useCallback( ( values ) => {
        // Gather data from rating form.
        const userRating = values?.rating ? values.rating : 0;
        const userReview = values?.review ? values.review : undefined;

        // Validate form data.
        const isValidData = ( userRating > 0 );
        if ( isValidData ) {
            ( async () => {
                // Set rating form as loading.
                setRatingFormLoading( true );

                // Make actual course service request to submit user rating.
                const response = await submitCourseRating( courseId, userRating, userReview );
                const hasError = !response;

                if ( !hasError ) {
                    closeRatingForm();

                    if ( !data.ratingAverage ) {
                        setAverageRating( userRating );
                    }

                }

                setRatingFormLoading( false );
            } )();
        }
    }, [ courseId, closeRatingForm, refetch ] );

    const ratingHasCommentsError = ratingFormData[ 'review' ] ? ( ratingFormData[ 'review' ].length > 100 ) : false;

    
    /**
     * Whether or not the user's rating submission is valid.
     * @type { boolean }
     */
    const isValidRatingSubmission = useMemo( () => {
        const userRating = ( ratingFormData?.rating ) ? ratingFormData.rating : 0;
        const userReview = ( ratingFormData?.review ) ? ratingFormData.review : '';
        return ( userRating > 0 && userReview?.length <= 100 );
    }, [ ratingFormData ] );

    /**
     * Triggered when an open learning object is closed and the page should update.
     */
    const handleLearningObjectUpdate = useCallback( async () => {
        await refetch();
    }, [ refetch ] );

    const bannerImageUrl = useMemo( () => {
        if ( data ) {
            const url = CourseUtils.getHeroImageUrl( data );
            return url ? url : getDefaultCourseBanner( ACTIVE_THEME_NAME );
        } else {
            return undefined;
        }
    }, [ data ] );

    return (
        <PageContent className="page-course-details">
            <LoadingPopup visible={ loading } />
        
            { /* Banner */ }
            <PageContainer className="course-details-banner-container">
                <InfoBanner
                    title={ data?.name }
                    parentTitle={ 
                        data?.parents?.length > 0 ?
                            <>
                                <Translate.Text 
                                    value="page.curriculumDetails.sections.description.header" />
                                { data.parents.flatMap( ( p, i ) => ( [ ( i != 0 ? ',\u00A0' : '\u00A0' ), <Link key={ p.curriculumId } push to={ Routes.CurriculumDetails.buildPath( p.curriculumId ) }>{ p.name }</Link> ] ) ) }
                            </> :
                            <></>
                    }
                    hidePercentage={ true }
                    percentComplete={ data?.progress }
                    isLoading= { loading } 
                    locked={ data?.prerequisiteData?.locked }
                    imageSrc={ bannerImageUrl }
                /> 
            </PageContainer>

            { data?.prerequisiteData?.locked && 
                <div className='locked-content'>
                    <span>
                        <PaperIcon collection='ultimate' icon='Lock4' />
                        { 
                            data.prerequisiteData.operator == 'AND' ? <Translate.Text value='learningObject.locked.course.all'/> 
                                : <Translate.Text value='learningObject.locked.course.any'/> 
                        }                        
                        { data.prerequisiteData.prerequisites
                            .filter( r => !r.completed )
                            .flatMap( ( req, i ) => ( [ ( i != 0 ? ',\u00A0' : '\u00A0' ), <Link key={ req.id } push to={ req.link }>{ req.name }</Link> ] ) ) }

                    </span>
                </div>
            }

            <div className="course-details-container">

                { breadcrumbs }

                <div className='details'>
                    <Col xs={ 12 } sm={ 16 } md={ 18 } lg={ 20 } className='description'>
                        <h4>
                            <Translate.Text
                                className="anim-fade-in-fast"
                                value="page.courseDetails.sections.description.header" 
                            />
                        </h4>
                        <div className="anim-fade-in-fast">
                            <span dangerouslySetInnerHTML={ { __html: data?.description } } />
                        </div>
                    </Col>
                    <Col xs={ 12 } sm={ 8 } md={ 6 } lg={ 4 } className='stats'>
                            <div className="rating-block">
                                <Rate
                                    className="course-rating"
                                    allowHalf
                                    disabled
                                    value={ averageRating }
                                />
                                <p className="rating-label">
                                    <Translate.Text value="page.courseDetails.sections.ratings.averageRatingLabel" />
                                </p>
                            </div>
                        {   
                            /* Rating Button */
                            ( !isShowingRatingForm && data?.status == 'COMPLETED' ) &&
                                <div className="button-center">
                                    <Button
                                        className="learning-action rating-toggle "
                                        type="text"
                                        onClick={ () => setShowRatingForm( true ) }
                                    >
                                        <Translate.Text value="page.courseDetails.sections.ratings.actions.rateCourse.label" />
                                    </Button>
                                </div>
                        }

                        { data?.certificate?.hasCertificate && !data?.prerequisiteData?.locked &&
                            <Certificate 
                                isEarned={ data.certificate.earned } 
                                fetchCertificateUrl={ data.fetchCertificateUrl } >
                                    { 
                                        data.certificate.earned 
                                            ? <Translate.Text value='certificate.earned' />
                                            : <Translate.Text value='certificate.unearned' /> 
                                    }
                            </Certificate>
                        }

                    </Col>
                </div>
                <SimpleCollapse expand={ isShowingRatingForm }>
                    <Form
                        form={ ratingFormController }
                        name="course-evaluation-form"
                        onValuesChange={ updateFormValues }
                        onFinish={ handleRatingSubmission }
                    >
                        <Row> 
                            <Col xs={ 12 } sm={ 16 } md={ 18 } lg={ 20 }>
                                <h4>
                                    <Translate.Text value="page.courseDetails.sections.ratings.header" />
                                </h4>
                                
                                <p>
                                    <Translate.Text value="page.courseDetails.sections.ratings.body.description" />
                                </p>
                            
                                <Form.Item
                                    name="review"
                                    help={ ratingHasCommentsError
                                        ? <Translate.Text value="page.courseDetails.sections.ratings.actions.comments.error.maxLength" />
                                        : ''
                                    }
                                    validateStatus={ ratingHasCommentsError ? 'error' : '' }
                                >
                                    <TextArea
                                        header={
                                            <Translate.Text value="page.courseDetails.sections.ratings.actions.comments.label" />
                                        }
                                        disabled={ disableForm }
                                        maxCharacterCount={ 100 }
                                    />
                                </Form.Item>
                            </Col>
                        
                            <Col xs={ 12 } sm={ 8 } md={ 6 } lg={ 4 }>
                                <div className="rating-block">
                                    <Form.Item name="rating">
                                        <Rate
                                            className="course-rating"
                                            allowClear={ false }
                                            disabled={ disableForm }
                                        />
                                    </Form.Item>
                                </div>

                                <div className="action-button-group">
                                    <ActionButton
                                        type="button"
                                        onClick={ () => { ratingFormController.submit(); } }
                                        important
                                        blue
                                        disabled={ !isValidRatingSubmission || disableForm }
                                        loading={ ratingFormLoading }
                                    >
                                        <Translate.Text value="page.courseDetails.sections.ratings.actions.submit" />
                                    </ActionButton>
                                        
                                    <ActionButton
                                        className="cancel-button"
                                        onClick={ closeRatingForm }
                                        animate={ false }
                                        gray
                                    >
                                        <Translate.Text value="page.courseDetails.sections.ratings.actions.cancel" />
                                    </ActionButton>
                                </div>
                            </Col>
                        </Row>
                    </Form>
                </SimpleCollapse>

                

                
                <div className='course-list'>
                    <h4>
                        <Translate.Text
                            className="anim-fade-in-fast"
                            value="page.courseDetails.sections.learningObjects.header" 
                        />
                    </h4> 
                    { 
                        !loading && data?.learningObject ?
                            <div className="course-learning-list">
                                <LearningObjectItem
                                    key={ data.learningObject.id }
                                    data={ data.learningObject }
                                    learningObjectId={ data.learningObject.id }
                                    courseId={ data.id }
                                    courseData={ data }
                                    lessonData={ data.learningObject }
                                    locked={ data.prerequisiteData?.locked }
                                    label={ data?.name }
                                    isComplete={ data.learningObject.status == 'COMPLETED' }
                                    onUpdate={ handleLearningObjectUpdate }
                                />
                            </div> :
 
                        !loading ?
                            <Translate.Text
                                value="page.courseDetails.sections.learningObjects.body.noData"
                                className="anim-fade-in-fast"
                            /> : <></>
                    }
                </div>
                
                { !loading && 
                    <ResourceList 
                        resourceIds={ data?.resourceIds }
                    />
                }
            </div> 
            <Modal
                className={ `${ ACTIVE_THEME_NAME } error-modal` }
                title={ error?.titleKey ? <h3>{ getString( error.titleKey ) }</h3> : '' }
                visible={ !!error }
                onOk={ () => {} }
                onCancel={ () => {} }
                closable={ false }
                footer={ [
                    <Button key='home' type='primary' onClick={ () => Navigation.push( Routes.Home.buildPath() ) } className='primary' >
                        { getString( 'navigation.home' ) }
                    </Button>,
                    <Button key='back' type='secondary' onClick={ () => Navigation.back() } className='secondary' >
                        { getString( 'navigation.back' ) }
                    </Button>,
                ] }>

                { error?.descriptionKey ? getString( error.descriptionKey ) : '' }
                
            </Modal>
        </PageContent>
    );
};
